import {
  Checkbox,
  MessageBarType,
  SearchBox
} from '@fluentui/react';
import { MessageComponent } from '../../../../../../commons/component/MessageComponent';
import { ContractConcept } from '../../../../../../models/Entities/Contracts/ContractConcept';
import { useEffect, useMemo, useState } from 'react';

////i18Next
import { useTranslation } from 'react-i18next';


// styles
import '../../ProtocolContractFormComponent.css';
import './ProtocolConceptMultiSelector.css';
import { ProtocolConceptItemComponent } from './ProtocolConceptItemComponent';

export interface IProps {
  concepts: ContractConcept[];
  parentConceptId?: number;
  selectedConceptIds: number[];
  /** Ids of the associated concepts whose relationship is restricted to the owner share. */
  applyOnlyOnOwnerAmountConceptIds?: number[];
  showPercentage: boolean;
  onChangeConceptSelection?: (concepts: ContractConcept[]) => void;
  onChangeApplyOnlyOnOwnerAmount?: (applyOnlyOnOwnerAmount: boolean, conceptId: number) => void;
  onAddConcept?: (concept: ContractConcept) => void;
  onDeleteConcept?: (concept: ContractConcept) => void;
  onChangeParticipationPercentage?: (percentage: number, conceptId: number) => void;
  onCostParticipationActiveChange?: (isActive: boolean, conceptId: number) => void;
  onCostParticipationPercentageChange?: (percentage: number, conceptId: number) => void;
}

// Content signature of a list of ids, used to re-seed the local selection only
// when the incoming selection really changed.
const idsKey = (ids: number[]): string => [...ids].sort((a, b) => a - b).join(',');

export const ProtocolConceptMultiSelectorComponent = (props: IProps) => {

  const [selectedConceptIds, setSelectedConceptIds] = useState<number[]>(props.selectedConceptIds || []);
  const [searchText, setSearchText] = useState<string>('');
  const [ownerAmountConceptIds, setOwnerAmountConceptIds] = useState<number[]>(props.applyOnlyOnOwnerAmountConceptIds || []);

  //useTranslation()
  const [t] = useTranslation();

  const selectedKey = idsKey(props.selectedConceptIds || []);
  const conceptsKey = idsKey((props.concepts || []).map((concept: ContractConcept) => concept.id));
  const ownerAmountKey = idsKey(props.applyOnlyOnOwnerAmountConceptIds || []);

  /*
   * The selection is re-seeded from the props only when the saved selection, the
   * available concepts or the edited concept actually change. Depending on the
   * identity of props.concepts instead would reset every pending check on any
   * store dispatch - including the one the save button itself fires - which is
   * how checks used to vanish before being persisted.
   */
  useEffect(() => {
    setSelectedConceptIds(props.selectedConceptIds || []);
  }, [selectedKey, conceptsKey, props.parentConceptId]);

  useEffect(() => {
    setOwnerAmountConceptIds(props.applyOnlyOnOwnerAmountConceptIds || []);
  }, [ownerAmountKey, conceptsKey, props.parentConceptId]);

  const selectableConcepts = useMemo(
    () => (props.concepts || [])
      .filter((concept: ContractConcept) => concept.id !== props.parentConceptId)
      .map((concept: ContractConcept) => ({ ...concept, isSelected: selectedConceptIds.includes(concept.id) })),
    [props.concepts, props.parentConceptId, selectedConceptIds]
  );

  const visibleConcepts = selectableConcepts.filter((concept: ContractConcept) =>
    (concept.concept?.conceptName || '').toLowerCase().includes(searchText.toLowerCase())
  );

  const selectedCount = selectableConcepts.filter((concept: ContractConcept) => concept.isSelected).length;
  const allVisibleSelected = visibleConcepts.length > 0 && visibleConcepts.every((concept: ContractConcept) => concept.isSelected);

  // events
  const applySelection = (nextSelectedIds: number[]) => {
    setSelectedConceptIds(nextSelectedIds);
    /*
     * A setting on a concept that is no longer associated would be saved as a
     * dangling flag, so unchecking a concept also clears its configuration.
     */
    ownerAmountConceptIds
      .filter((conceptId: number) => !nextSelectedIds.includes(conceptId))
      .forEach((conceptId: number) => props.onChangeApplyOnlyOnOwnerAmount?.(false, conceptId));
    setOwnerAmountConceptIds(ownerAmountConceptIds.filter((conceptId: number) => nextSelectedIds.includes(conceptId)));
    props.onChangeConceptSelection?.(
      (props.concepts || [])
        .filter((concept: ContractConcept) => concept.id !== props.parentConceptId)
        .map((concept: ContractConcept) => ({ ...concept, isSelected: nextSelectedIds.includes(concept.id) }))
    );
  };

  const onCheckboxChange = (isChecked: boolean, ConceptId: number) => {
    applySelection(
      isChecked
        ? [...selectedConceptIds.filter((id: number) => id !== ConceptId), ConceptId]
        : selectedConceptIds.filter((id: number) => id !== ConceptId)
    );
  };

  const onApplyOnlyOnOwnerAmountChange = (applyOnlyOnOwnerAmount: boolean, conceptId: number) => {
    setOwnerAmountConceptIds(
      applyOnlyOnOwnerAmount
        ? [...ownerAmountConceptIds.filter((id: number) => id !== conceptId), conceptId]
        : ownerAmountConceptIds.filter((id: number) => id !== conceptId)
    );
    props.onChangeApplyOnlyOnOwnerAmount?.(applyOnlyOnOwnerAmount, conceptId);
  };

  const onSelectAllChange = (isChecked: boolean) => {
    const visibleIds = visibleConcepts.map((concept: ContractConcept) => concept.id);
    applySelection(
      isChecked
        ? [...selectedConceptIds.filter((id: number) => !visibleIds.includes(id)), ...visibleIds]
        : selectedConceptIds.filter((id: number) => !visibleIds.includes(id))
    );
  };

  const Controls = (
    <div className='ConceptMultiSelectorToolbar'>
      <div className='ConceptMultiSelectorSearch'>
        <SearchBox placeholder={t('protocolConceptMultiselector.controls.searchConcept')} onChange={(
          event?: React.ChangeEvent<HTMLInputElement> | undefined, newValue?: string | undefined
        ) => setSearchText(newValue || '') } />
      </div>
      <div className='ConceptMultiSelectorActions'>
        <Checkbox
          className='ConceptMultiSelectorSelectAll'
          label={t('protocolConceptMultiselector.controls.selectAll')}
          checked={allVisibleSelected}
          disabled={visibleConcepts.length === 0}
          onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => onSelectAllChange(checked || false)}
        />
        <span className='ConceptMultiSelectorCounter'>
          {t('protocolConceptMultiselector.controls.selectedCounter', { selected: selectedCount, total: selectableConcepts.length })}
        </span>
      </div>
    </div>
  );

  return (
    <div className="ConceptMultiSelector">
      {Controls}
      <div className="ConceptMultiSelectorList">
        {visibleConcepts.length > 0 && (
          <ul>
            {visibleConcepts.map((concept: ContractConcept) => (
              <li key={'li' + concept.id} className={concept.isSelected ? 'ConceptMultiSelectorItem Selected' : 'ConceptMultiSelectorItem'}>
                <ProtocolConceptItemComponent
                  concept={concept}
                  isSelected={concept.isSelected}
                  applyOnlyOnOwnerAmount={ownerAmountConceptIds.includes(concept.id)}
                  onApplyOnlyOnOwnerAmountChange={props.onChangeApplyOnlyOnOwnerAmount ? onApplyOnlyOnOwnerAmountChange : undefined}
                  onCheckboxChange={onCheckboxChange} />
              </li>
            ))}
          </ul>
        )}
        {selectableConcepts.length > 0 && visibleConcepts.length === 0 && (
          <p className='ConceptMultiSelectorEmpty'>{t('protocolConceptMultiselector.controls.noResults')}</p>
        )}
        {selectableConcepts.length === 0 && (
          <MessageComponent
            message={t('protocolConceptMultiselector.controls.conceptMessage')}
            type={MessageBarType.info}
          />
        )}
      </div>
    </div>
  );
};
